import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { Queue } from '@rossum/api-client/queues';
import { Header } from '@rossum/components/header/Header';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { Button } from '@rossum/ui/material';
import { InitialCopilot } from '../../../rules/copilots/InitialCopilot';
import { RulesList } from '../../../rules/rules-list/RulesList';
import { QueueSettingsBreadcrumbs } from '../../components/QueueSettingsBreadcrumbs';
import { QueueSettingsTabs } from '../../components/QueueSettingsTabs';

type RulesPageProps = {
  queue: Queue;
  parentPath: string;
  workspaceName: string;
};

export const RulesPage = ({
  queue,
  parentPath,
  workspaceName,
}: RulesPageProps) => {
  const intl = useIntl();
  const history = useHistory();

  const copilotOpen = history.location.hash === '#new';
  const setCopilotOpen = useCallback(
    (open: boolean) => {
      history.replace({ hash: open ? '#new' : '' });
    },
    [history]
  );

  return (
    <PageLayout
      renderHeader={params => (
        <Header
          scrollableDivRef={params.scrollableDivRef}
          tabs={<QueueSettingsTabs value="rules" parentPath={parentPath} />}
          breadcrumbs={
            <QueueSettingsBreadcrumbs
              breadcrumbs={[
                {
                  label: intl.formatMessage({
                    id: 'features.queueSettings.rules.list.title',
                  }),
                },
              ]}
              queue={queue}
              workspaceName={workspaceName}
            />
          }
          buttons={[
            <Button
              key="create-rule"
              variant="contained"
              onClick={() => setCopilotOpen(true)}
              data-cy="add-rule-button"
              sx={{
                '&:hover': {
                  color: 'inherit',
                },
              }}
            >
              {intl.formatMessage({
                id: 'features.queueSettings.rules.list.addRule',
              })}
            </Button>,
          ]}
          title={intl.formatMessage({
            id: 'features.queueSettings.rules.list.title',
          })}
          description={intl.formatMessage({
            id: 'features.queueSettings.rules.list.description',
          })}
        />
      )}
    >
      <RulesList queue={queue} onCreateRule={() => setCopilotOpen(true)} />
      <InitialCopilot
        defaultQueue={queue.url}
        allowQueueChange={false}
        onSuccess={value => {
          setCopilotOpen(false);
          history.push({
            pathname: `${parentPath}/rules/new`,
            state: { rule: value },
          });
        }}
        onClose={() => setCopilotOpen(false)}
        open={copilotOpen}
      />
    </PageLayout>
  );
};
